import * as React from 'react';
import styled from 'styled-components';
import { Button } from '../button/Button';
import { AnimatedCollapsibleContent } from '../collapsible/AnimatedCollapsibleContent';
import { colors } from '../colors/color';
import { Icon, iconName } from '../icons/Icons';

export type ListRowStyleVariant = 'outlined' | 'lightGray';

type ListRowProps = {
    styleVariant?: ListRowStyleVariant,
    stripColor?: string,
    icon?: iconName,
    iconColor?: string,
    level?: 1 | 2 | 3,
    padding?: string,

    collapsed?: boolean,
    onCollapse?: (collapsed: boolean) => void,

    onClick?: () => void,

    content?: React.ReactNode,
    childRows?: React.ReactNode;
};

export function ListRow (props: ListRowProps): JSX.Element
{
    const [collapsed, setCollapsed] = React.useState<boolean>(props.collapsed || false);
    React.useEffect(() =>
    {
        setCollapsed(props.collapsed || false);
    }, [props.collapsed]);

    function handleClickCollapse ()
    {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        props.onCollapse?.(newCollapsed);
    }

    return (
        <div style={{ marginLeft: getLeftMargin(props.level || 1) }}>

            <InnerListRow{...props} onCollapse={handleClickCollapse} />

            {props.childRows &&
                <AnimatedCollapsibleContent open={collapsed === undefined || collapsed === false}>
                    {props.childRows}
                </AnimatedCollapsibleContent>
            }
        </div>
    );
}

function InnerListRow (props: ListRowProps): JSX.Element
{
    const [collapsed, setCollapsed] = React.useState<boolean>(false);
    React.useEffect(() =>
    {
        setCollapsed(props.collapsed || false);
    }, [props.collapsed]);

    function handleClickCollapse (e: React.MouseEvent<HTMLButtonElement>)
    {
        e.stopPropagation();
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed)
        props.onCollapse?.(newCollapsed);
    }

    return (
        <StyledListRowContainer 
            level={props.level || 1} 
            styleVariant={props.styleVariant || 'lightGray'}
            onClick={props.onClick}
            hasOnClick={props.onClick !== undefined}>
                
                <ColorStrip className="strip_color" color={props.stripColor || 'transparent'} styleVariant={props.styleVariant || 'lightGray'} />

                {props.childRows && (
                    <Button variant='text'
                        size='32px'
                        icon={collapsed  ? 'arrows_chevron_chevronDown' : 'arrows_chevron_chevronUp'}
                        onClick={handleClickCollapse} />
                )}
                
                {props.icon &&
                    <Icon name={props.icon} color={props.iconColor !== undefined ? props.iconColor : colors.icon.default} />
                }

                <div style={{ width: '100%', padding: props.padding || '8px' }}>
                    {props.content}
                </div> 

        </StyledListRowContainer>
    );
}


function getLeftMargin(level: 1 | 2 | 3): string
{
    if (level === 3)
        return '16px';
    if (level === 2)
        return '8px';
    return '0px';
}

const ColorStrip = styled.div<{ color: string, styleVariant: ListRowStyleVariant }>`
    margin-left: 4px;
    width: 4px;
    flex: 0 0 4px;
    align-self: stretch;
    margin-top: 4px;
    margin-bottom: 4px;
    background-color: ${props => props.color || 'transparent'};
`;

const StyledListRowContainer = styled.div<{ level:1 | 2 | 3, styleVariant:ListRowStyleVariant, hasOnClick: boolean; }>`
    display: flex;
    flex-direction: row;
    justify-content: stretch;
    align-items: center;
    margin-bottom: 1px;

    cursor: ${props => props.hasOnClick ? 'pointer' : 'default'};

    border: 1px solid ${colors.listRow.normal.border};
    border-radius: 4px;
    background-color: ${props => {
        if(props.styleVariant === 'lightGray')
            return colors.listRow.lightGray.background;
        else
            return colors.listRow.normal.background;
    }};
    ${props => props.hasOnClick && `
        &:hover {
            background-color: ${props.styleVariant === 'lightGray' 
                ? colors.listRow.lightGray.backgroundHover 
                : colors.listRow.normal.backgroundHover};
        }
    `}
`;